import * as React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sheet from '@mui/joy/Sheet';
import CssBaseline from '@mui/joy/CssBaseline';
import Typography from '@mui/joy/Typography';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Link from '@mui/joy/Link';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import Auth from '../../utils/auth';
import ReCAPTCHA from 'react-google-recaptcha';
const SITE_KEY = '6LdhiQUrAAAAAPtJtmz4BW8sc-9aFDvTDNem1DG0';

export default function LogInComponent({ admin = false }) {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [captchaToken, setCaptchaToken] = useState('');
    const navigate = useNavigate();
    const captchaRef = React.useRef();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };
    const handleCaptcha = (token) => {
        setCaptchaToken(token);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // CAPTCHA check only for normal users
        if (!admin && failedAttempts >= 2 && !captchaToken) {
            setError('Please complete the CAPTCHA.');
            return;
        }

        setLoading(true);

        // pick the right login method
        const loginFn = admin
            ? () => Auth.adminLogin(formData.email, formData.password)
            : () =>
                Auth.login(
                    formData.email,
                    formData.password,
                    failedAttempts >= 2 ? captchaToken : null
                );

        const { success, message } = await loginFn();

        if (success) {
            setFailedAttempts(0);
            navigate('/');
        } else {
            setFailedAttempts((prev) => prev + 1);
            setError(message);
            captchaRef.current?.reset();
            setCaptchaToken('');
        }

        setLoading(false);
    };

    return (
        <main>
            <CssBaseline />
            <Sheet
                sx={{
                    width: 400,
                    height: 'auto',
                    mx: 'auto',
                    my: 4,
                    py: 3,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                }}
                variant="outlined"
            >
                <div>
                    <Typography level="h4" component="h1" sx={{ marginBottom: '10px', color: 'rgba(41, 27, 59, 1)', fontSize: '1.4rem' }}>
                        <b>Welcome!</b>
                    </Typography>
                    <Typography level="body-sm" sx={{ marginBottom: '20px', fontSize: '1rem' }}>Sign in to continue.</Typography>
                </div>

                {error && <Alert color="danger" variant="soft">{error}</Alert>}

                <form onSubmit={handleSubmit}>
                    <FormControl sx={{ marginBottom: '20px' }}>
                        <FormLabel>Email</FormLabel>
                        <Input
                            name="email"
                            type="email"
                            placeholder="example@email.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>
                    <FormControl sx={{ marginBottom: '20px' }}>
                        <FormLabel>Password</FormLabel>
                        <Input
                            name="password"
                            type="password"
                            placeholder="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                    </FormControl>
                    {failedAttempts >= 2 && (
                        <div style={{ marginBottom: '20px' }}>
                            <ReCAPTCHA
                                ref={captchaRef}
                                sitekey={SITE_KEY}
                                onChange={handleCaptcha}
                            />
                        </div>
                    )}

                    <Button
                        type="submit"
                        sx={{ mt: 1, backgroundColor: 'rgba(87, 42, 241, 1)', marginBottom: '20px' }}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size="sm" color="neutral" /> : 'Log in'}
                    </Button>
                </form>

                <Typography
                    endDecorator={<Link href="https://go.clientbloom.ai/" sx={{ color: 'rgba(87, 42, 241, 1)' }}>Sign up</Link>}
                    sx={{ fontSize: 'sm', alignSelf: 'center' }}
                >
                    Don&apos;t have an account?
                </Typography>
            </Sheet>
        </main>
    );
}